import { useState, useEffect, useRef } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

const CATEGORIES = [
  { key: 'MARKETING', label: 'Marketing', desc: 'Send promotions or announcements to increase awareness and engagement' },
  { key: 'UTILITY', label: 'Utility', desc: 'Send messages about an existing order or account.' },
  { key: 'AUTHENTICATION', label: 'Authentication', desc: 'Send codes for verification or login.' },
];

const LANGUAGES = [
  { code: 'en_US', label: 'English (US) (en_US)' },
  { code: 'en', label: 'English (en)' },
  { code: 'ta', label: 'Tamil (ta)' },
  { code: 'hi', label: 'Hindi (hi)' },
  { code: 'ml', label: 'Malayalam (ml)' },
  { code: 'te', label: 'Telugu (te)' },
  { code: 'kn', label: 'Kannada (kn)' },
];

const BUTTON_TYPES = [
  { type: 'QUICK_REPLY', label: '↩ Quick Reply' },
  { type: 'PHONE_NUMBER', label: '📞 Call' },
  { type: 'URL', label: '🌐 Website' },
  { type: 'COPY_CODE', label: '📋 Copy Code' },
  { type: 'FLOW', label: '📝 Flow' },
  { type: 'VOICE_CALL', label: '📞 Voice Call' },
  { type: 'MPM', label: '🛍 Products' },
  { type: 'CATALOG', label: '📚 Catalog' },
  { type: 'LOCATION', label: '📍 Location' },
  { type: 'OTP', label: '🔐 OTP' },
];

const CARD_ICONS = {
  order: '📋', cart: '🛒', ship: '🚚', welcome: '🌸', bee: '🌺',
  confirm: '✅', remind: '⏰', track: '📍', default: '💬',
};

function getIcon(name) {
  if (!name) return CARD_ICONS.default;
  const n = name.toLowerCase();
  if (n.includes('order')) return '📋';
  if (n.includes('cart') || n.includes('checkout')) return '🛒';
  if (n.includes('ship') || n.includes('track')) return '🚚';
  if (n.includes('welcome')) return '🌸';
  if (n.includes('confirm')) return '✅';
  if (n.includes('remind')) return '⏰';
  return '💬';
}

function getIconBg(name) {
  if (!name) return '#ede9fe';
  const n = name.toLowerCase();
  if (n.includes('order')) return '#EAF3DE';
  if (n.includes('cart') || n.includes('checkout')) return '#FFF3E0';
  if (n.includes('welcome')) return '#EDE9FE';
  if (n.includes('confirm')) return '#EAF3DE';
  return '#f1f5f9';
}

export default function Templates() {
  const { selectedBusiness, user } = useAuth();
  const bid = user?.role === 'admin'
    ? (selectedBusiness?.id || selectedBusiness?.business_id)
    : user?.business_id;

  const [activeTab, setActiveTab] = useState('MARKETING');
  const [step, setStep] = useState(1);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [checked, setChecked] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('en_US');
  const [headerType, setHeaderType] = useState('none');
  const [headerUrl, setHeaderUrl] = useState('');
  const [headerMediaId, setHeaderMediaId] = useState('');
  const [body, setBody] = useState('');
  const [footer, setFooter] = useState('');
  const [buttons, setButtons] = useState([]);
  const [creating, setCreating] = useState(false);
  const [isNewChat, setIsNewChat] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState({ text: '', type: '' });
  const fileRef = useRef();

  useEffect(() => { if (bid) fetchTemplates(); }, [bid]);

  const fetchTemplates = async () => {
    try {
      setTemplates([]);
      const r = await api.get('/templates?business_id=' + bid);
      setTemplates(r.data || []);
    } catch { setTemplates([]); }
  };

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 3500);
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/templates/${t.name}?business_id=${bid}`);
      showMsg('Template deleted!');
      fetchTemplates();
    } catch(e) { showMsg(e.response?.data?.error || 'Delete failed', 'error'); }
  };

  const handleTabChange = (key) => { setActiveTab(key); setStep(1); setChecked(false); };
  const handleNext = () => { if (!checked) { showMsg('Please select Custom to continue', 'error'); return; } setStep(2); };
  const handleBack = () => { setStep(1); setChecked(false); };

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const toBase64 = f => new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result.split(',')[1]);
        r.onerror = rej;
        r.readAsDataURL(f);
      });
      const base64 = await toBase64(file);
      const r = await api.post('/templates/upload', {
        file_base64: base64, file_name: file.name, file_type: file.type, business_id: bid
      }, { timeout: 180000 });
      setHeaderUrl(r.data.public_url || r.data.url || 'uploaded');
      setHeaderMediaId(r.data.media_id || '');
      showMsg('File uploaded!');
    } catch (e) { showMsg('Upload failed: ' + (e.response?.data?.error || e.message), 'error'); }
    setUploading(false);
  };

  const addVariable = () => {
    const vars = (body.match(/\{\{\d+\}\}/g) || []);
    setBody(prev => prev + `{{${vars.length + 1}}}`);
  };

  const addButton = (type) => {
    if (buttons.length >= 3) { showMsg('Max 3 buttons allowed', 'error'); return; }
    const defaults = {
      QUICK_REPLY: { type, text: '' },
      PHONE_NUMBER: { type, text: 'Call Us', phone_number: '' },
      URL: { type, text: 'Visit Website', url: '' },
      COPY_CODE: { type, text: 'Copy Code', example: '' },
      FLOW: { type, text: 'Open Flow', flow_id: '' },
      VOICE_CALL: { type, text: 'Voice Call', phone_number: '' },
      MPM: { type, text: 'View Products' },
      CATALOG: { type, text: 'View Catalog' },
      LOCATION: { type, text: 'Share Location' },
      OTP: { type, text: 'Copy OTP', otp_type: 'COPY_CODE' },
    };
    setButtons(prev => [...prev, defaults[type] || { type, text: '' }]);
  };

  const updateBtn = (i, field, val) => {
    setButtons(prev => prev.map((b, idx) => idx === i ? { ...b, [field]: val } : b));
  };

  const createTemplate = async () => {
    if (!name || !body) { showMsg('Name and body required', 'error'); return; }
    setCreating(true);
    try {
      await api.post('/templates', {
        name: name.toLowerCase().replace(/\s+/g, '_'),
        body, category: activeTab, language,
        header_type: headerType !== 'none' ? headerType : null,
        header_url: headerUrl || null,
        header_media_id: headerMediaId || null,
        footer: footer || null,
        buttons: buttons.length ? buttons : undefined,
        is_new_chat: isNewChat ? 1 : 0,
        business_id: bid,
      });
      showMsg('Template created! Pending Meta approval.');
      setName(''); setBody(''); setFooter(''); setButtons([]);
      setHeaderType('none'); setHeaderUrl(''); setHeaderMediaId('');
      setStep(1); setChecked(false); setIsNewChat(false);
      fetchTemplates();
    } catch (e) { showMsg(e.response?.data?.error || e.message, 'error'); }
    setCreating(false);
  };

  const filteredTemplates = templates.filter(t => (t.category || '').toUpperCase() === activeTab);
  const approvedCount = filteredTemplates.filter(t => t.status === 'APPROVED').length;
  const reviewCount = filteredTemplates.filter(t => t.status === 'IN_REVIEW' || t.status === 'PENDING').length;
  const rejectedCount = filteredTemplates.filter(t => t.status === 'REJECTED').length;

  const inp = {
    width: '100%', padding: '10px 14px', border: '1px solid #d1d5db',
    borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none',
    fontFamily: 'inherit', color: '#111', background: '#fff',
  };

  const cat = CATEGORIES.find(c => c.key === activeTab);

  return (
    <div style={{ padding: '28px 32px', fontFamily: 'DM Sans, sans-serif', background: '#f4f6f9', minHeight: '100vh' }}>

      {msg.text && (
        <div style={{ position: 'fixed', top: 20, right: 24, zIndex: 9999, background: msg.type === 'error' ? '#fee2e2' : '#dcfce7', color: msg.type === 'error' ? '#b91c1c' : '#15803d', padding: '12px 22px', borderRadius: 10, fontWeight: 600, fontSize: 14, boxShadow: '0 4px 20px rgba(0,0,0,0.13)' }}>{msg.text}</div>
      )}

      <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 20, color: '#111' }}>Templates</h2>

      {/* Create Template Card — unchanged */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', overflow: 'hidden', marginBottom: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
        <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', padding: '0 20px' }}>
          {CATEGORIES.map(c => (
            <button key={c.key} onClick={() => handleTabChange(c.key)} style={{ padding: '14px 22px', fontSize: 14, fontWeight: 600, cursor: 'pointer', background: 'none', border: 'none', color: activeTab === c.key ? '#16a34a' : '#6b7280', borderBottom: activeTab === c.key ? '2px solid #16a34a' : '2px solid transparent', transition: 'all 0.15s' }}>{c.label}</button>
          ))}
        </div>
        <div style={{ padding: '24px' }}>
          {step === 1 && (
            <div style={{ background: '#f0fdf4', borderRadius: 10, padding: '20px 24px' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#15803d', marginBottom: 14 }}>{cat.label}</div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} style={{ width: 18, height: 18, accentColor: '#16a34a', marginTop: 3, flexShrink: 0, cursor: 'pointer' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: '#15803d', fontSize: 14 }}>Custom</div>
                  <div style={{ fontSize: 14, color: '#6b7280', marginTop: 2 }}>{cat.desc}</div>
                </div>
                <button onClick={handleNext} style={{ background: checked ? '#16a34a' : '#9ca3af', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 28px', fontWeight: 600, fontSize: 14, cursor: checked ? 'pointer' : 'not-allowed', flexShrink: 0 }}>Next</button>
              </div>
            </div>
          )}
          {step === 2 && (
            <div style={{ display: 'flex', gap: 24 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 22 }}>
                <button onClick={handleBack} style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 15, fontWeight: 600, color: '#374151' }}>← Back</button>
                <span style={{ fontSize: 15, fontWeight: 600, color: '#15803d' }}>Fill Template Details</span>
              </div>
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', gap: 16 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 15, fontWeight: 600, color: '#15803d', marginBottom: 6, display: 'block' }}>Template Name</label>
                    <input style={inp} placeholder="Enter Template Name" value={name} onChange={e => setName(e.target.value.toLowerCase().replace(/\s+/g, '_'))} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 15, fontWeight: 600, color: '#15803d', marginBottom: 6, display: 'block' }}>Language</label>
                    <select style={{ ...inp, cursor: 'pointer' }} value={language} onChange={e => setLanguage(e.target.value)}>
                      {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 15, fontWeight: 600, color: '#15803d', marginBottom: 8, display: 'block' }}>Header</label>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                  {[{ val: 'none', label: 'None' }, { val: 'text', label: 'Text' }, { val: 'image', label: 'Image' }, { val: 'video', label: 'Video' }, { val: 'document', label: 'Document' }].map(opt => (
                    <button key={opt.val} onClick={() => { setHeaderType(opt.val); setHeaderUrl(''); setHeaderMediaId(''); }} style={{ padding: '8px 18px', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer', border: headerType === opt.val ? '2px solid #16a34a' : '1px solid #d1d5db', background: headerType === opt.val ? '#f0fdf4' : '#fff', color: headerType === opt.val ? '#15803d' : '#374151' }}>{opt.label}</button>
                  ))}
                </div>
                {headerType === 'text' && <input style={inp} placeholder="Header text" value={headerUrl} onChange={e => setHeaderUrl(e.target.value)} />}
                {['image', 'video', 'document'].includes(headerType) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                    <input type="file" ref={fileRef} style={{ display: 'none' }} accept={headerType === 'image' ? 'image/*' : headerType === 'video' ? 'video/*' : '.pdf,.doc,.docx'} onChange={e => e.target.files[0] && uploadFile(e.target.files[0])} />
                    <button onClick={() => fileRef.current.click()} style={{ padding: '8px 20px', borderRadius: 8, border: '1.5px dashed #d1d5db', background: '#f9fafb', cursor: 'pointer', fontSize: 15, fontWeight: 600, color: '#374151' }}>{uploading ? 'Uploading...' : headerUrl ? '✓ Uploaded' : 'Upload File'}</button>
                    {headerUrl && <span style={{ fontSize: 14, color: '#16a34a', fontWeight: 600 }}>File ready!</span>}
                  </div>
                )}
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 15, fontWeight: 600, color: '#15803d', marginBottom: 8, display: 'block' }}>Body</label>
                <textarea style={{ ...inp, minHeight: 120, resize: 'vertical' }} placeholder="Template body text... Use {{1}}, {{2}} for variables" value={body} onChange={e => setBody(e.target.value)} />
                <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                  <button onClick={() => setBody(p => `*${p}*`)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 900, fontSize: 14, color: '#374151', padding: '2px 6px' }}>B</button>
                  <button onClick={() => setBody(p => `_${p}_`)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontStyle: 'italic', fontSize: 14, color: '#374151', padding: '2px 6px' }}>I</button>
                  <button onClick={addVariable} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, color: '#16a34a', fontWeight: 600 }}>+ Add Variable</button>
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 15, fontWeight: 600, color: '#15803d', marginBottom: 8, display: 'block' }}>Footer</label>
                <input style={inp} placeholder="Footer text (optional)" value={footer} onChange={e => setFooter(e.target.value)} />
              </div>
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label style={{ fontSize: 15, fontWeight: 700, color: '#111' }}>Buttons</label>
                  <button onClick={() => addButton('QUICK_REPLY')} style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 18px', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>Add Button</button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: buttons.length ? 14 : 0 }}>
                  {BUTTON_TYPES.map(bt => (
                    <button key={bt.type} onClick={() => addButton(bt.type)} style={{ padding: '6px 14px', borderRadius: 20, border: '1.5px solid #e5e7eb', background: '#f9fafb', color: '#374151', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>+ {bt.label}</button>
                  ))}
                </div>
                {buttons.map((btn, i) => (
                  <div key={i} style={{ background: '#f9fafb', borderRadius: 10, padding: 14, marginBottom: 8, border: '1px solid #e5e7eb', marginTop: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#6b7280', background: '#e5e7eb', padding: '3px 10px', borderRadius: 20 }}>{btn.type}</span>
                      <button onClick={() => setButtons(prev => prev.filter((_, j) => j !== i))} style={{ background: '#fee2e2', border: 'none', color: '#b91c1c', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontWeight: 700 }}>✕</button>
                    </div>
                    <input placeholder="Button Text" value={btn.text} onChange={e => updateBtn(i, 'text', e.target.value)} style={{ ...inp, marginBottom: 6 }} />
                    {btn.type === 'PHONE_NUMBER' && <input placeholder="Phone (+919940407341)" value={btn.phone_number || ''} onChange={e => updateBtn(i, 'phone_number', e.target.value)} style={inp} />}
                    {btn.type === 'URL' && <input placeholder="URL (https://example.com)" value={btn.url || ''} onChange={e => updateBtn(i, 'url', e.target.value)} style={inp} />}
                    {btn.type === 'COPY_CODE' && <input placeholder="Coupon Code" value={btn.example || ''} onChange={e => updateBtn(i, 'example', e.target.value)} style={inp} />}
                    {btn.type === 'FLOW' && <input placeholder="Flow ID" value={btn.flow_id || ''} onChange={e => updateBtn(i, 'flow_id', e.target.value)} style={inp} />}
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, padding:'14px 16px', background:'#f0fdf4', borderRadius:10, border:'1.5px solid #bbf7d0' }}>
                <div onClick={() => setIsNewChat(p => !p)} style={{ width:44, height:24, borderRadius:12, background: isNewChat ? '#16a34a' : '#d1d5db', cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}>
                  <div style={{ width:20, height:20, borderRadius:'50%', background:'#fff', position:'absolute', top:2, left: isNewChat ? 22 : 2, transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
                </div>
                <div>
                  <div style={{ fontSize:14, fontWeight:700, color:'#15803d' }}>Use for New Chat</div>
                  <div style={{ fontSize:12, color:'#6b7280' }}>Enable to show this template in New Chat</div>
                </div>
              </div>
              <button onClick={createTemplate} disabled={creating || uploading} style={{ width: '100%', padding: 14, borderRadius: 10, border: 'none', background: creating ? '#9ca3af' : '#16a34a', color: '#fff', fontWeight: 700, fontSize: 15, cursor: creating ? 'not-allowed' : 'pointer' }}>
                {creating ? 'Creating...' : 'Create Template'}
              </button>
              </div>
              <div style={{ width: 320, flexShrink: 0, position: 'sticky', top: 20, alignSelf: 'flex-start' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#6b7280', marginBottom: 10, textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Live Preview</div>
                <div style={{ borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
                  <div style={{ background: '#075E54', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#fff' }}>V</div>
                    <div style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>VBOT Business</div>
                  </div>
                  <div style={{ background: '#e5ddd5', padding: 10, minHeight: 200 }}>
                    <div style={{ background: '#fff', borderRadius: '0 10px 10px 10px', maxWidth: '95%', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                      {headerType !== 'none' && (
                        <div style={{ background: '#f0f0f0', padding: '8px 12px', borderBottom: '1px solid #e0e0e0' }}>
                          {headerType === 'text' && <div style={{ fontWeight: 700, fontSize: 14, color: '#111' }}>{headerUrl || 'Header text'}</div>}
                          {headerType === 'image' && <div style={{ textAlign: 'center', color: '#666', fontSize: 12 }}>🖼️ Image Header</div>}
                          {headerType === 'video' && <div style={{ textAlign: 'center', color: '#666', fontSize: 12 }}>🎥 Video Header</div>}
                          {headerType === 'document' && <div style={{ textAlign: 'center', color: '#666', fontSize: 12 }}>📄 Document Header</div>}
                        </div>
                      )}
                      <div style={{ padding: '10px 12px', fontSize: 13, lineHeight: 1.6, color: '#111', whiteSpace: 'pre-wrap', minHeight: 40 }}>
                        {body || 'Template body text will appear here...'}
                      </div>
                      {footer ? <div style={{ padding: '4px 12px 8px', fontSize: 11, color: '#8696a0', borderTop: '1px solid #f0f0f0' }}>{footer}</div> : null}
                      {buttons.length > 0 && (
                        <div style={{ borderTop: '1px solid #e0e0e0' }}>
                          {buttons.map((btn, i) => (
                            <div key={i} style={{ padding: '8px 12px', textAlign: 'center', color: '#0093FF', fontWeight: 600, fontSize: 13, borderBottom: i < buttons.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
                              {btn.text || 'Button'}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stats Row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total', value: filteredTemplates.length, color: '#16a34a', dot: '#16a34a' },
          { label: 'Approved', value: approvedCount, color: '#22c55e', dot: '#22c55e' },
          { label: 'In Review', value: reviewCount, color: '#f59e0b', dot: '#f59e0b' },
          { label: 'Rejected', value: rejectedCount, color: '#ef4444', dot: '#ef4444' },
        ].map((s, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: 12, padding: '14px 18px', flex: 1, border: '0.5px solid #ddd6fe', display: 'flex', alignItems: 'center', gap: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Cards Grid Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#111' }}>Existing Templates ({filteredTemplates.length})</div>
        <button onClick={fetchTemplates} style={{ padding: '6px 16px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' }}>Refresh</button>
      </div>

      {/* Cards Grid */}
      {filteredTemplates.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: 14, padding: 60, textAlign: 'center', color: '#9ca3af', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>📋</div>
          <div style={{ fontSize: 14 }}>No {activeTab.toLowerCase()} templates yet</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 24 }}>
          {filteredTemplates.map((t, i) => (
            <div key={i} style={{ background: '#fff', borderRadius: 14, border: '0.5px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', transition: 'box-shadow 0.2s, transform 0.15s', cursor: 'default' }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.05)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
              <div style={{ height: 3, background: t.status === 'APPROVED' ? '#1D9E75' : t.status === 'REJECTED' ? '#ef4444' : '#f59e0b' }} />
              <div style={{ padding: 14 }}>
              {/* Card Top */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10, gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 9, background: getIconBg(t.name), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>{getIcon(t.name)}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', wordBreak: 'break-word' }}>{t.name}</div>
                </div>
                <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 500, flexShrink: 0, background: t.status === 'APPROVED' ? '#EAF3DE' : t.status === 'REJECTED' ? '#FCEBEB' : '#fef9c3', color: t.status === 'APPROVED' ? '#3B6D11' : t.status === 'REJECTED' ? '#A32D2D' : '#854d0e' }}>
                  {t.status === 'APPROVED' ? 'Approved' : t.status}
                </span>
              </div>
              {/* Body */}
              <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.6, marginBottom: 12, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: 38 }}>
                {t.components?.find(c => c.type === 'BODY')?.text || '—'}
              </div>
              {/* Footer */}
              <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 500, background: '#f1f5f9', color: '#475569' }}>{t.category}</span>
                  <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 500, background: '#f1f5f9', color: '#475569' }}>{t.language}</span>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => setPreviewTemplate(t)} style={{ padding: '4px 10px', borderRadius: 6, border: '0.5px solid #7F77DD', background: '#EDE9FE', color: '#7F77DD', fontSize: 10, fontWeight: 500, cursor: 'pointer' }}>Preview</button>
                  <button onClick={() => deleteTemplate(t)} style={{ padding: '4px 8px', borderRadius: 6, border: '0.5px solid #fecaca', background: '#fff0f0', color: '#dc2626', fontSize: 10, cursor: 'pointer' }}>Del</button>
                </div>
              </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      {previewTemplate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 16, width: 360, maxWidth: '90vw', overflow: 'hidden', border: '0.5px solid #e5e7eb', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ padding: '14px 16px', borderBottom: '0.5px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#111', display: 'flex', alignItems: 'center', gap: 6 }}>👁 Preview</div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{previewTemplate.name} · {previewTemplate.category} · {previewTemplate.language}</div>
              </div>
              <button onClick={() => setPreviewTemplate(null)} style={{ border: '0.5px solid #e5e7eb', background: '#fff', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 13, color: '#374151' }}>✕</button>
            </div>
            <div style={{ padding: 16, background: '#e5ddd5', minHeight: 120 }}>
              <div style={{ background: '#fff', borderRadius: '0 10px 10px 10px', padding: '12px 14px', maxWidth: '88%', display: 'inline-block', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
                {previewTemplate.components?.find(c => c.type === 'HEADER') && (
                  <div style={{ fontWeight: 500, marginBottom: 6, color: '#111', fontSize: 13 }}>
                    {previewTemplate.components.find(c => c.type === 'HEADER').text || previewTemplate.components.find(c => c.type === 'HEADER').format}
                  </div>
                )}
                <div style={{ fontSize: 12, color: '#111', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {previewTemplate.components?.find(c => c.type === 'BODY')?.text || 'No body text'}
                </div>
                {previewTemplate.components?.find(c => c.type === 'FOOTER') && (
                  <div style={{ fontSize: 10, color: '#aaa', marginTop: 4 }}>
                    {previewTemplate.components.find(c => c.type === 'FOOTER').text}
                  </div>
                )}
                <div style={{ fontSize: 10, color: '#aaa', textAlign: 'right', marginTop: 4 }}>
                  {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} ✓✓
                </div>
              </div>
              {previewTemplate.components?.filter(c => c.type === 'BUTTONS')?.flatMap(c => c.buttons || []).map((btn, i) => (
                <div key={i} style={{ background: '#fff', borderRadius: 8, padding: '7px 14px', marginTop: 4, textAlign: 'center', color: '#2563eb', fontSize: 12, fontWeight: 500, maxWidth: '88%' }}>
                  {btn.text}
                </div>
              ))}
            </div>
            <div style={{ padding: '12px 16px', borderTop: '0.5px solid #e5e7eb', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 500, background: previewTemplate.status === 'APPROVED' ? '#EAF3DE' : previewTemplate.status === 'REJECTED' ? '#FCEBEB' : '#fef9c3', color: previewTemplate.status === 'APPROVED' ? '#3B6D11' : previewTemplate.status === 'REJECTED' ? '#A32D2D' : '#854d0e' }}>{previewTemplate.status}</span>
              <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 500, background: '#f1f5f9', color: '#475569' }}>{previewTemplate.category}</span>
              <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 500, background: '#f1f5f9', color: '#475569' }}>{previewTemplate.language}</span>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}
